import { StringHelpers } from "../helpers/string-helpers";
import { TraceLoggingHelpers } from "../helpers/trace-logging-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { LimitAlertModel } from "../models/limit-alert/limit-alert.model";
import { IComponent } from "./component.interface";
import { MainSymbolCurrentPriceComponent } from "./main-symbol-current-price.component";

export class LimitAlertPlotLineComponent implements IComponent {
    public id = StringHelpers.newGuid();;

    public moveMode = false;
    public updatedPrice: number | null = null;
    public selected = false;
    public model: LimitAlertModel;

    private base: IChartContainer;
    private plotLine: any = null;
    private plotLineClone: any = null;
    private cloneId = '';

    private mouseUpHandler: EventListener; 

    constructor(base: IChartContainer, limitAlert: LimitAlertModel) {
        this.base = base;
        this.model = limitAlert;
        this.id = 'limit-alert-' + this.id;
        this.cloneId = this.id + '-clone';

        this.mouseUpHandler = this.mouseUp.bind(this);

        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            this.setIconPosition();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            this.setIconPosition();
        });
        this.createSytle();
        this.init();
        this.onEvents();
        this.onDeletePress();
        this.onBodyClick();
    }

    public remove() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        yAxis.removePlotLine(this.id);
        yAxis.removePlotLine(this.cloneId);
        this.removeStyle();

        this.base.tiChartSeries.removeSubscriberByKey(this.id);
        this.base.domEvents.removeSubscribersByKey(this.id);
        this.base.tiChartSeries.element.removeEventListener('mouseup', this.mouseUpHandler);
    }

    private removeStyle() {
        const highChartStyle = this.base.tiChartSeries?.element?.findFirst('.style-' + this.id);
        if (highChartStyle) {
            highChartStyle.remove();
        }
    }

    private createSytle() {
        this.removeStyle();
        const className = this.id;
        let styleTag = `<style>`;
        styleTag += `.highcharts-plot-line.${className} {stroke-width: 20px !important; stroke: transparent !important; cursor: move;}`;

        if (this.base.marketingWebChartMode) {
            let color = this.model.color.toRGB();
            if (this.model.isTriggered()) {
                const lastPriceUpColor = this.base.tiChartSeries?.chartStyleSettings?.getLastPriceUpColor();
                color = lastPriceUpColor ? lastPriceUpColor : '#32cd32';
            }

            styleTag += `.highcharts-plot-line.${className}-clone {stroke: ${color} !important;}`;
        } else {
            styleTag += `.highcharts-plot-line.${className}-clone {stroke: ${this.model.color.toRGB()} !important;}`;
        }
        styleTag += `</style>`;

        const chartStyleContainer = `<div class="style-${className}">${styleTag}</div>`;
        this.base.tiChartSeries?.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    private plotLineMouseOut: any;
    private preventHideRemoveIcon = false;
    private init() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (yAxis) {
            yAxis.addPlotLine({
                value: this.model.price,
                className: this.id,
                width: 1,
                id: this.id,
                zIndex: 13,
                label: {
                    formatter: () => {
                        return ' ';
                    },
                    className: 'label-' + this.id,
                    useHTML: true
                },
                events: {
                    mouseover: () => {
                        this.showTooltip();
                        this.showRemoveIcon();
                    },
                    mouseout: () => {
                        clearTimeout(this.plotLineMouseOut);
                        this.plotLineMouseOut = setTimeout(() => {
                            this.hideRemoveIcon();
                        }, 300);
                    },
                    mousedown: () => {
                        this.moveMode = true;
                        this.base.tiChartSeries.preventDragging = true;
                    }
                }
            });

            yAxis.addPlotLine({
                value: this.model.price,
                className: this.cloneId,
                id: this.cloneId,
                zIndex: 12
            });

            this.bindEvents();
            this.setIconPosition();
        }
    }

    private mouseUp() {        
        if (!this.moveMode) {
            return;
        }

        this.moveMode = false;
        this.unselectPlotLine();
        this.base.chartService.requestUpdateLimitAlert(this.model.id, this.model.price);

        (this.base.getComponentsByType(MainSymbolCurrentPriceComponent)?.[0] as MainSymbolCurrentPriceComponent)?.showAddButton();
        setTimeout(() => {
            this.base.tiChartSeries.preventDragging = false;
        }, 500);
    }

    private onEvents() {
        // if (!this.model.preCreate) return;
        this.base.domEvents.onMouseMove(this.id, (event: any) => {
            if (!this.moveMode || !this.base.tiChartSeries) {
                return;
            }

            const yAxis: any = this.base.tiChartSeries.highStockChart.yAxis[0];
            if (!yAxis) {
                return;
            }

            (this.base.getComponentsByType(MainSymbolCurrentPriceComponent)?.[0] as MainSymbolCurrentPriceComponent)?.hideAddButton();
            const pointer = this.base.tiChartSeries.highStockChart.pointer.normalize(event);

            const parentElementRect = this.base.tiChartSeries.chartElement.getBoundingClientRect();
            const yTop = pointer.y - parentElementRect.y;

            let yValue = yAxis.toValue(yTop);

            this.model.price = yValue;
            this.updatedPrice = yValue;
            yAxis.removePlotLine(this.id);
            yAxis.addPlotLine({
                value: yValue,
                className: this.id,
                width: 1,
                id: this.id,
                zIndex: 13,
                label: {
                    formatter: () => {
                        return '&nbsp;';
                    },
                    className: 'label-' + this.id,
                    useHTML: true,
                },
                events: {
                    mouseover: () => {
                        this.showTooltip();
                        this.showRemoveIcon();
                    },
                    mouseout: () => {
                        clearTimeout(this.plotLineMouseOut);
                        this.plotLineMouseOut = setTimeout(() => {
                            this.hideRemoveIcon();
                        }, 300);
                    },
                    mousedown: () => {
                        this.moveMode = true;
                        this.base.tiChartSeries.preventDragging = true;
                    }
                }
            });

            yAxis.removePlotLine(this.cloneId);
            yAxis.addPlotLine({
                value: this.model.price,
                className: this.cloneId,
                id: this.cloneId,
                zIndex: 12
            });

            this.bindEvents();
            this.setIconPosition();
        });

        this.base.tiChartSeries.element.addEventListener('mouseup', this.mouseUpHandler);
    }

    private onDeletePress() {
        this.base.domEvents.onDeletePress(this.id, () => {
            TraceLoggingHelpers.log('requestRemoveLimitAlert ', this.selected)
            if (this.selected) {
                this.base.chartService.requestRemoveLimitAlert(this.model.id);
                this.unselectPlotLine();
            }
        });
    }

    private onBodyClick() {
        this.base.domEvents.onScreenClick(this.id, (event: any) => {
            // if ((!event.target || !event.target.classList.contains(this.id))
            //     && !(this.selected && event.target && event.target.classList.contains('highcharts-annotation-remove-button'))) {
            //     this.unselectPlotLine();
            // }
            
            if (!event.target || !event.target.classList.contains(this.id)) {
                this.unselectPlotLine();
            } else {
                this.selectPlotLine();
            }
        });
    }

    private selectPlotLine() {
        this.selected = true;
        this.plotLine && this.plotLine.classList.add('selected');
        this.plotLineClone && this.plotLineClone.classList.add('selected');
    }

    private unselectPlotLine() {
        this.selected = false;
        this.plotLine && this.plotLine.classList.remove('selected');
        this.plotLineClone && this.plotLineClone.classList.remove('selected');
    }

    private bindEvents() {
        this.getLimitAletSVGAndAddTooltip();

        if (this.plotLine) {
            this.plotLine.addEventListener('click', () => {
                if (this.plotLine.classList.contains('selected')) {
                    this.unselectPlotLine();
                } else {
                    this.selectPlotLine();
                }
            });
        }

        if (this.selected) {
            this.selectPlotLine();
        }
    }

    private setIconPosition() {
        if (this.selected) {
            this.selectPlotLine();
        }
        
        const chartStyle = this.base.tiChartSeries?.chartStyleSettings;
        this.base.tiChartSeries.element.findFirst(`.highcharts-plot-line-label.label-${this.id}`)?.ifExistThen((el: HTMLElement) => {
            el.querySelectorAll('img').forEach((i: HTMLImageElement) => {
                i.remove();
            });

            let left = 0;
            if (this.base.pnpMode) {
                left = this.base.tiChartSeries.element.clientWidth - 77;

                if (chartStyle.pnpChangePercentEnabled) {
                    left -= 6;
                }
            } else {
                const chartExtraSmall = this.base.isExtraSmall();
                left = this.base.tiChartSeries.element.clientWidth - (chartStyle?.showSmartRiskLevel ? 112 : 82);
                if (chartExtraSmall) {
                    left = this.base.tiChartSeries.element.clientWidth - 34;
                }
            }

            if (this.base.marketingWebChartMode) {
                const style = `position: absolute;left: ${left}px; top: 13px;`;
                el.innerHTML += `<img src="${this.model.getImage(this.base.basePath)}" style="${style}" title="${this.model.tooltip}" />`;

                let color = this.model.color.toRGB();
                if (this.model.isTriggered()) {
                    const lastPriceUpColor = this.base.tiChartSeries?.chartStyleSettings?.getLastPriceUpColor();
                    color = lastPriceUpColor ? lastPriceUpColor : '#32cd32';
                }
                
                el.querySelectorAll('.remove').forEach((el: Element) => {
                    el.remove();
                });

                el.innerHTML += `
                <div class="remove" style="display: none;">
                    <svg style="color:#fff;cursor:pointer;background:${color};width:10px;height:10px;position: absolute;left: ${left - 32}px; top: 12px;">
                        <use href="${this.base.basePath}assetfiles/img/closev2.svg#closev2"></use>
                    </svg>
                </div>`;
                setTimeout(() => {
                    el.findFirst('.remove')?.ifExistThen((b: HTMLElement) => {
                        if (b.containClass('inited')) {
                            return;
                        }
                        b.addClass('inited');
                        b.addEventListener('click', (e: any) => {
                            e.preventDefault();
                            e.stopPropagation();

                            this.base.chartService.requestRemoveLimitAlert(this.model.id);

                            this.remove();
                        });
                        b.addEventListener('mouseenter', () => {
                            this.preventHideRemoveIcon = true;
                        });
                        b.addEventListener('mouseout', () => {
                            this.preventHideRemoveIcon = false;
                            this.hideRemoveIcon();
                        });
                    })
                }, 300);
                // }
            } else {
                const style = `position: absolute;left: ${left}px; top: 12px;`;
                el.innerHTML += `<img src="${this.model.getImage(this.base.basePath)}" style="${style}" title="${this.model.tooltip}" />`;
            }
        });

        this.getLimitAletSVGAndAddTooltip();
    }

    private getLimitAletSVGAndAddTooltip() {
        this.plotLine = this.base.tiChartSeries.element.findFirst('.' + this.id);
        this.plotLineClone = this.base.tiChartSeries.element.findFirst('.' + this.id + '-clone');
    }

    private showTooltip() {
        if (this.plotLine) {
            const titleElement = document.createElementNS('http://www.w3.org/2000/svg', 'title');
            titleElement.textContent = this.model.tooltip;

            const plotLineElement = this.plotLine as SVGPathElement;
            const groupElement = plotLineElement.parentElement;
            if (groupElement) {
                groupElement.querySelectorAll('title').forEach((t: Element) => {
                    t.remove();
                });
                groupElement?.append(titleElement);
            }
        }
    }

    private showRemoveIcon() {
        this.base.tiChartSeries.element.findFirst(`.highcharts-plot-line-label.label-${this.id} .remove`)?.ifExistThen((el: HTMLElement) => {
            el.style.display = '';
        });
    }

    private hideRemoveIcon() {
        if (this.preventHideRemoveIcon) return;
        this.base.tiChartSeries.element.findFirst(`.highcharts-plot-line-label.label-${this.id} .remove`)?.ifExistThen((el: HTMLElement) => {
            el.style.display = 'none';
        });
    }
}